import React, { useEffect, useState } from 'react'
import {
  Button,
  Table,
  Modal,
  Input,
  Select,
  Upload,
  message,
  Radio,
  Space,
  InputNumber,
  Popconfirm,
  Tag,
  Row,
  Col,
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  DeleteFilled,
  EditOutlined,
  UploadOutlined,
  SearchOutlined,
  ProfileOutlined,
  ApartmentOutlined,
  PictureOutlined,
  SettingOutlined,
  DashboardOutlined,
  AimOutlined,
  TableOutlined,
} from '@ant-design/icons'
import axios from 'axios'
import { constants } from '../../constants'
import SessionStorage from '../../util/sessionstore'
import ApolloClientService from '../../services/apolloclient.service'
import {
  cardStyle,
  sectionHeaderRow,
  sectionTitle,
  fieldLabelStyle,
  iconBadge,
} from '../../component/schedule/tracpatch.config.styles'
import ExerciseInstructionsModal from '../../component/exercise/exercise.instructions.modal'

const apolloService = new ApolloClientService()
const session = new SessionStorage()

// ---- Media guidelines (mirrored on the server in /upload/exercisemedia) -----
const IMAGE_DIM = 510 // max width/height, px (image may be smaller, any aspect)
const IMAGE_MAX_BYTES = 2 * 1024 * 1024 // 2 MB
const VIDEO_MAX_BYTES = 50 * 1024 * 1024 // 50 MB
const MAX_SEQUENCE = 12
const IMAGE_TYPES = ['image/png', 'image/jpeg']
const VIDEO_TYPES = ['video/mp4']

// Stored media is a /content-relative path; prefix the API origin to render it.
const toMediaUrl = (p) => {
  if (!p || p === 'NA') return ''
  if (/^https?:\/\//.test(p)) return p
  return `${constants.API_URL}${p.startsWith('/') ? '' : '/'}${p}`
}

// Resolve an image's natural dimensions in the browser.
const probeImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Could not read image'))
    }
    img.src = url
  })

// Validate an image against format / max 510x510 / 2 MB. Returns true or shows a
// message and returns false. Dimensions may be anything up to 510×510 px (each
// side ≤ 510); larger images are rejected.
const validateImage = async (file) => {
  if (!IMAGE_TYPES.includes(file.type)) {
    message.error('Image must be PNG or JPEG')
    return false
  }
  if (file.size > IMAGE_MAX_BYTES) {
    message.error('Image must be 2 MB or smaller')
    return false
  }
  try {
    const { width, height } = await probeImage(file)
    if (width > IMAGE_DIM || height > IMAGE_DIM) {
      message.error(`Image must be ${IMAGE_DIM}×${IMAGE_DIM} px or smaller (got ${width}×${height})`)
      return false
    }
  } catch (e) {
    message.error('Could not read image dimensions')
    return false
  }
  return true
}

const validateVideo = (file) => {
  if (!VIDEO_TYPES.includes(file.type)) {
    message.error('Video must be MP4 (H.264)')
    return false
  }
  if (file.size > VIDEO_MAX_BYTES) {
    message.error('Video must be 50 MB or smaller')
    return false
  }
  return true
}

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

// POST a base64 data URL to the server and return the stored relative path.
const uploadMedia = async ({ kind, dataUrl, exerciseKey, role }) => {
  const res = await axios.post(`${constants.API_URL}/upload/exercisemedia`, {
    kind,
    data: dataUrl,
    exerciseKey,
    role,
  }, {
    headers: { 'x-authorization': 'Bearer ' + session.getProfile()?.token },
  })
  if (res.data && res.data.status === 'success') return res.data.path
  throw new Error((res.data && res.data.message) || 'Upload failed')
}

const POSTURES = ['seated', 'supine', 'standing']
const MODES = ['reps', 'hold', 'rom']
const MILESTONES = ['yellow_start', 'yellow_end', 'green_end']

const emptyTracpatch = () => ({
  posture: 'seated',
  mode: 'reps',
  extension_limit: 0,
  extension_low: 40,
  extension_high: 20,
  flexion_low: 50,
  flexion_high: 70,
  flexion_limit: 130,
  bandsRequired: ['ext', 'flex'],
  requiredReps: 10,
  requiredHoldSeconds: undefined,
  requiredSets: 1,
  restAngle: 90,
  tierDefaults: [],
})

// Merge a stored config over the defaults WITHOUT letting null/undefined fields
// blank out a sensible default — otherwise editing an exercise whose stored
// requiredReps/angles are null would silently clear them and the server's
// validation (reps>0, monotonic chain) would reject the save.
const mergeTracpatch = (cfg) => {
  const base = emptyTracpatch()
  if (!cfg) return base
  Object.keys(base).forEach((k) => {
    if (cfg[k] !== undefined && cfg[k] !== null) base[k] = cfg[k]
  })
  if (Array.isArray(cfg.bandsRequired)) base.bandsRequired = cfg.bandsRequired
  if (Array.isArray(cfg.tierDefaults)) base.tierDefaults = cfg.tierDefaults.map((r) => ({ ...r }))
  return base
}

// The server's canonical angle chain (physio_server/data/models/exercise.js).
const ANGLE_CHAIN = [
  ['extension_limit', 'Ext limit'],
  ['extension_high', 'Ext high'],
  ['extension_low', 'Ext low'],
  ['flexion_low', 'Flex low'],
  ['flexion_high', 'Flex high'],
  ['flexion_limit', 'Flex limit'],
]

// Pre-flight validation mirroring the server so the user sees WHY a save would
// be rejected instead of a generic failure. Returns an error string or null.
const tracpatchError = (tp) => {
  for (let i = 1; i < ANGLE_CHAIN.length; i++) {
    const [ka, la] = ANGLE_CHAIN[i - 1]
    const [kb, lb] = ANGLE_CHAIN[i]
    const a = tp[ka]
    const b = tp[kb]
    if (a == null || b == null) return `${la} and ${lb} must both be set`
    if (!(a <= b)) return `${la} (${a}°) must be ≤ ${lb} (${b}°)`
  }
  if (tp.mode === 'reps' && !(tp.requiredReps > 0)) return "Mode 'reps' needs Required reps > 0"
  if (tp.mode === 'hold' && (!(tp.requiredHoldSeconds > 0) || !(tp.requiredReps > 0)))
    return "Mode 'hold' needs Hold seconds > 0 and Required reps > 0"
  return null
}

const blankForm = () => ({
  _id: null,
  name: '',
  description: '',
  type: 'standard',
  image: '',
  imagesequence: [],
  video: '',
  surgeryTypeIds: [],
  tracpatchConfig: emptyTracpatch(),
})

export default function ExerciseLibrary() {
  const [exercises, setExercises] = useState([])
  const [surgeryTypes, setSurgeryTypes] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [uploadKey, setUploadKey] = useState('new')
  const [form, setForm] = useState(blankForm())
  // Read-only instructions preview (what the patient sees), opened on row click.
  const [viewExercise, setViewExercise] = useState(null)

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }))
  const setTp = (key, value) =>
    setForm((f) => ({ ...f, tracpatchConfig: { ...f.tracpatchConfig, [key]: value } }))

  const loadExercises = async () => {
    setLoading(true)
    try {
      const res = await apolloService.getAllExercises()
      setExercises((res && res.exercises) || [])
    } catch (e) {
      message.error('Failed to load exercises')
    }
    setLoading(false)
  }

  const loadSurgeryTypes = async () => {
    try {
      const types = await apolloService.getActiveSurgeryTypes()
      setSurgeryTypes(Array.isArray(types) ? types : [])
    } catch (e) {
      setSurgeryTypes([])
    }
  }

  useEffect(() => {
    loadExercises()
    loadSurgeryTypes()
  }, [])

  const openCreate = () => {
    setForm(blankForm())
    setUploadKey(`tmp_${Date.now()}`)
    setModalOpen(true)
  }

  const openEdit = (ex) => {
    setForm({
      _id: ex._id,
      name: ex.name || '',
      description: ex.description || '',
      type: ex.type || 'standard',
      image: ex.image && ex.image !== '/exdefault.png' ? ex.image : '',
      imagesequence: Array.isArray(ex.imagesequence) ? ex.imagesequence : [],
      video: ex.video && ex.video !== 'NA' ? ex.video : '',
      surgeryTypeIds: Array.isArray(ex.surgeryTypes) ? ex.surgeryTypes.map((s) => s._id) : [],
      tracpatchConfig:
        ex.type === 'TracPatch' && ex.tracpatchConfig
          ? mergeTracpatch(ex.tracpatchConfig)
          : emptyTracpatch(),
    })
    setUploadKey(ex.exerciseId ? `ex_${ex.exerciseId}` : `id_${ex._id}`)
    setModalOpen(true)
  }

  // ---- Upload handlers (return false to stop antd's own upload) ------------
  const beforeMainImage = async (file) => {
    if (!(await validateImage(file))) return Upload.LIST_IGNORE
    try {
      const dataUrl = await fileToDataUrl(file)
      const path = await uploadMedia({ kind: 'image', dataUrl, exerciseKey: uploadKey, role: 'main' })
      setField('image', path)
      message.success('Main image uploaded')
    } catch (e) {
      message.error(e.message || 'Upload failed')
    }
    return Upload.LIST_IGNORE
  }

  const beforeSequenceImage = async (file) => {
    if (form.imagesequence.length >= MAX_SEQUENCE) {
      message.error(`Up to ${MAX_SEQUENCE} sequence images`)
      return Upload.LIST_IGNORE
    }
    if (!(await validateImage(file))) return Upload.LIST_IGNORE
    try {
      const dataUrl = await fileToDataUrl(file)
      const path = await uploadMedia({ kind: 'image', dataUrl, exerciseKey: uploadKey, role: 'seq' })
      setForm((f) => ({ ...f, imagesequence: [...f.imagesequence, path] }))
      message.success('Sequence image added')
    } catch (e) {
      message.error(e.message || 'Upload failed')
    }
    return Upload.LIST_IGNORE
  }

  const beforeVideo = async (file) => {
    if (!validateVideo(file)) return Upload.LIST_IGNORE
    try {
      const dataUrl = await fileToDataUrl(file)
      const path = await uploadMedia({ kind: 'video', dataUrl, exerciseKey: uploadKey, role: 'video' })
      setField('video', path)
      message.success('Video uploaded')
    } catch (e) {
      message.error(e.message || 'Upload failed')
    }
    return Upload.LIST_IGNORE
  }

  const removeSequenceAt = (idx) =>
    setForm((f) => ({ ...f, imagesequence: f.imagesequence.filter((_, i) => i !== idx) }))

  const moveSequence = (idx, delta) =>
    setForm((f) => {
      const next = [...f.imagesequence]
      const j = idx + delta
      if (j < 0 || j >= next.length) return f
      ;[next[idx], next[j]] = [next[j], next[idx]]
      return { ...f, imagesequence: next }
    })

  // ---- TierDefaults table editing -----------------------------------------
  const addTier = () =>
    setTp('tierDefaults', [
      ...(form.tracpatchConfig.tierDefaults || []),
      {
        tier: (form.tracpatchConfig.tierDefaults || []).length + 1,
        weekFrom: 0,
        weekTo: null,
        flexion_low: 50,
        flexion_high: 70,
        extension_low: 40,
        extension_high: 20,
        rep_milestone: 'yellow_start',
        reps: 10,
      },
    ])

  const setTierField = (idx, key, value) =>
    setTp(
      'tierDefaults',
      (form.tracpatchConfig.tierDefaults || []).map((r, i) => (i === idx ? { ...r, [key]: value } : r))
    )

  const removeTier = (idx) =>
    setTp('tierDefaults', (form.tracpatchConfig.tierDefaults || []).filter((_, i) => i !== idx))

  // ---- Save / delete -------------------------------------------------------
  const handleSave = async () => {
    if (!form.name.trim() || !form.description.trim()) {
      message.error('Name and description are required')
      return
    }
    const input = {
      name: form.name.trim(),
      description: form.description.trim(),
      type: form.type,
      // Clearing media must send the field's DEFAULT SENTINEL, not '' or
      // undefined. _mapExerciseInput only writes a field when it's != null, so
      // `undefined` left the old media in place; and `video` is `required: true`
      // in the model, whose validator REJECTS '' ("video is required") — so a
      // cleared video must round-trip as its 'NA' sentinel (= "no video"), and a
      // cleared image as '/exdefault.png'. On create these equal the model
      // defaults, so create behavior is unchanged. openEdit maps both sentinels
      // back to '' in the form, so the fields still show empty on re-edit.
      image: form.image || '/exdefault.png',
      imagesequence: form.imagesequence,
      video: form.video || 'NA',
      surgeryTypeIds: form.surgeryTypeIds,
    }
    if (form.type === 'TracPatch') {
      const tp = form.tracpatchConfig
      const tpErr = tracpatchError(tp)
      if (tpErr) {
        message.error(tpErr)
        return
      }
      input.tracpatchConfig = {
        posture: tp.posture,
        mode: tp.mode,
        extension_limit: tp.extension_limit,
        extension_low: tp.extension_low,
        extension_high: tp.extension_high,
        flexion_low: tp.flexion_low,
        flexion_high: tp.flexion_high,
        flexion_limit: tp.flexion_limit,
        bandsRequired: tp.bandsRequired,
        requiredReps: tp.requiredReps,
        requiredHoldSeconds: tp.requiredHoldSeconds,
        requiredSets: tp.requiredSets,
        restAngle: tp.restAngle,
        // Map each row to ONLY the TierDefaultIn fields. Rows loaded from the
        // server carry Apollo's `__typename`, which isn't a valid input field
        // and makes the GraphQL request 400 on variable coercion.
        tierDefaults: (tp.tierDefaults || []).map((r) => ({
          tier: r.tier,
          weekFrom: r.weekFrom,
          weekTo: r.weekTo,
          flexion_low: r.flexion_low,
          flexion_high: r.flexion_high,
          extension_low: r.extension_low,
          extension_high: r.extension_high,
          rep_milestone: r.rep_milestone,
          reps: r.reps,
        })),
      }
    }
    setSaving(true)
    try {
      const res = form._id
        ? await apolloService.updateExercise(form._id, input)
        : await apolloService.createExercise(input)
      if (res && res.status === constants.MESSAGE.SUCCESS) {
        message.success(form._id ? 'Exercise updated' : 'Exercise created')
        setModalOpen(false)
        loadExercises()
      } else {
        message.error((res && res.message) || 'Save failed')
      }
    } catch (e) {
      // A 400 (variable/validation rejection) carries its detail under
      // networkError.result.errors, not graphQLErrors.
      const netErrors =
        e && e.networkError && e.networkError.result && e.networkError.result.errors
      const msg =
        (e && e.graphQLErrors && e.graphQLErrors[0] && e.graphQLErrors[0].message) ||
        (netErrors && netErrors[0] && netErrors[0].message) ||
        (e && e.message) ||
        'Save failed'
      console.error('Exercise save error:', e)
      message.error(msg)
    }
    setSaving(false)
  }

  const handleDelete = async (ex) => {
    try {
      const res = await apolloService.deleteExercise(ex._id)
      if (res && res.status === constants.MESSAGE.SUCCESS) {
        message.success('Exercise deleted')
        loadExercises()
      } else {
        message.error((res && res.message) || 'Delete failed')
      }
    } catch (e) {
      message.error('Delete failed')
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'exerciseId', width: 70 },
    {
      title: 'Image',
      dataIndex: 'image',
      width: 80,
      render: (img) =>
        toMediaUrl(img) ? (
          <img src={toMediaUrl(img)} alt='' style={{ width: 44, height: 44, objectFit: 'cover' }} />
        ) : (
          <span style={{ color: '#bbb' }}>—</span>
        ),
    },
    { title: 'Name', dataIndex: 'name' },
    {
      title: 'Type',
      dataIndex: 'type',
      width: 110,
      render: (t) => <Tag color={t === 'TracPatch' ? 'geekblue' : 'default'}>{t}</Tag>,
    },
    {
      title: 'Disciplines',
      dataIndex: 'surgeryTypes',
      // Match each row's Type tag colour (geekblue for TracPatch, default otherwise).
      render: (types, record) =>
        Array.isArray(types) && types.length
          ? types.map((s) => (
              <Tag key={s._id} color={record.type === 'TracPatch' ? 'geekblue' : 'default'}>
                {s.name}
              </Tag>
            ))
          : <span style={{ color: '#bbb' }}>—</span>,
    },
    {
      title: 'Actions',
      width: 260,
      render: (_, ex) => (
        // Stop row-click (which opens the read-only instructions preview) from
        // also firing when the Edit / Delete controls are used.
        <Space onClick={(e) => e.stopPropagation()}>
          <Button size='small' onClick={() => setViewExercise(ex)}>
            View instructions
          </Button>
          <Button size='small' icon={<EditOutlined />} onClick={() => openEdit(ex)}>
            Edit
          </Button>
          <Popconfirm title='Delete this exercise?' onConfirm={() => handleDelete(ex)}>
            <Button size='small' danger icon={<DeleteFilled />} />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // Local heading / subheading colors (kept local so the shared override form
  // styles are untouched). Headings = indigo accent, subheadings = slate.
  const sectionHeading = { ...sectionTitle, color: '#001429' }
  const subHeadingColor = '#64748b'

  // Card header with an indigo icon badge + uppercase title.
  const cardTitle = (icon, text) => (
    <div style={sectionHeaderRow}>
      <span style={iconBadge('#e6f4ff')}>
        {React.cloneElement(icon, { style: { color: '#001429' } })}
      </span>
      <span style={sectionHeading}>{text}</span>
    </div>
  )

  const mediaHint = { color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 8 }
  const mediaThumb = {
    width: 76,
    height: 76,
    objectFit: 'cover',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    display: 'block',
    background: '#f1f5f9',
  }
  const uploadTile = {
    width: 76,
    height: 76,
    borderRadius: 10,
    border: '1px dashed #cbd5e1',
    background: '#f8fafc',
    color: '#64748b',
  }

  // Shared table styling for the TracPatch tabular sections (mirrors the
  // override form's REP MILESTONE table look).
  const tableBox = { border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff' }
  const tableHeadRow = {
    background: '#f8fafc',
    borderBottom: '1px solid #e5e7eb',
    padding: '10px 9px 10px 12px',
    alignItems: 'center',
    margin: 0,
  }
  const tableBodyRow = { padding: '8px 9px 8px 12px', borderBottom: '1px solid #f1f5f9', margin: 0 }
  const tableHeaderStyle = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: '#64748b',
    whiteSpace: 'nowrap',
  }

  // Live pre-flight check shown as a banner inside the TracPatch section.
  const tpLiveError = form.type === 'TracPatch' ? tracpatchError(form.tracpatchConfig) : null

  // Filter the table by name, type, or discipline name (case-insensitive),
  // then sort by exerciseId ascending.
  const q = search.trim().toLowerCase()
  const filteredExercises = (!q
    ? exercises
    : exercises.filter((ex) => {
        const names = [
          ex.name || '',
          ex.type || '',
          ...(Array.isArray(ex.surgeryTypes) ? ex.surgeryTypes.map((s) => (s && s.name) || '') : []),
        ]
        return names.some((n) => n.toLowerCase().includes(q))
      })
  )
    .slice()
    .sort((a, b) => (a.exerciseId || 0) - (b.exerciseId || 0))

  return (
    <div style={{ padding: 24 }} className='ex-navy'>
      {/* Recolor antd's default blue to the navbar navy, scoped to this page
          (.ex-navy) and the exercise modal (.ex-navy-modal via wrapClassName)
          so it never leaks to the rest of the admin. */}
      <style>{`
        .ex-navy .ant-btn-primary:not([disabled]),
        .ex-navy-modal .ant-btn-primary:not([disabled]) { background:#001429; border-color:#001429; }
        .ex-navy .ant-btn-primary:not([disabled]):hover,
        .ex-navy .ant-btn-primary:not([disabled]):focus,
        .ex-navy-modal .ant-btn-primary:not([disabled]):hover,
        .ex-navy-modal .ant-btn-primary:not([disabled]):focus { background:#0a3a5c; border-color:#0a3a5c; }
        .ex-navy-modal .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
          background:#001429; border-color:#001429;
        }
        .ex-navy-modal .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)::before { background:#001429; }
        .ex-navy-modal .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled):hover {
          background:#0a3a5c; border-color:#0a3a5c;
        }
        .ex-navy .ant-input:hover, .ex-navy .ant-input:focus, .ex-navy .ant-input-focused,
        .ex-navy-modal .ant-input:hover, .ex-navy-modal .ant-input:focus, .ex-navy-modal .ant-input-focused,
        .ex-navy-modal .ant-input-number:hover, .ex-navy-modal .ant-input-number-focused,
        .ex-navy .ant-select:not(.ant-select-disabled):hover .ant-select-selector,
        .ex-navy .ant-select-focused:not(.ant-select-disabled) .ant-select-selector,
        .ex-navy-modal .ant-select:not(.ant-select-disabled):hover .ant-select-selector,
        .ex-navy-modal .ant-select-focused:not(.ant-select-disabled) .ant-select-selector {
          border-color:#001429 !important; box-shadow:none !important;
        }
        .ex-navy-modal .ant-select-multiple .ant-select-selection-item {
          background:#e6edf3; border-color:#001429; color:#001429;
        }
        .ex-navy-modal .ant-btn-dashed:hover, .ex-navy-modal .ant-btn-dashed:focus { color:#001429; border-color:#001429; }
        .ex-navy .ant-pagination-item-active { border-color:#001429; }
        .ex-navy .ant-pagination-item-active a { color:#001429; }
        /* Slightly round every field & button. */
        .ex-navy .ant-input, .ex-navy .ant-input-affix-wrapper, .ex-navy .ant-input-number, .ex-navy .ant-select-selector, .ex-navy .ant-btn,
        .ex-navy-modal .ant-input, .ex-navy-modal .ant-input-affix-wrapper, .ex-navy-modal .ant-input-number,
        .ex-navy-modal .ant-select-selector, .ex-navy-modal .ant-btn, .ex-navy-modal textarea.ant-input {
          border-radius:8px !important;
        }
        .ex-navy-modal .ant-radio-button-wrapper:first-child { border-radius:8px 0 0 8px; }
        .ex-navy-modal .ant-radio-button-wrapper:last-child { border-radius:0 8px 8px 0; }
        /* Rounded tags — both the list Type/Disciplines tags and the modal multi-select tags. */
        .ex-navy .ant-tag,
        .ex-navy-modal .ant-select-multiple .ant-select-selection-item { border-radius:6px; }

        /* Lighter, greyish text inside input fields. */
        .ex-navy-modal .ant-input,
        .ex-navy-modal .ant-input-number-input,
        .ex-navy-modal .ant-select-single .ant-select-selection-item { color:#64748b; }
        .ex-navy-modal .ant-input::placeholder { color:#a8b3c2; }
        /* Bin / delete icons are filled orange with no button border/background... */
        .ex-navy .anticon-delete, .ex-navy-modal .anticon-delete { color:#f5881f !important; }
        .ex-navy .ant-btn-dangerous:not(.media-del), .ex-navy .ant-btn-dangerous:not(.media-del):hover, .ex-navy .ant-btn-dangerous:not(.media-del):focus,
        .ex-navy-modal .ant-btn-dangerous:not(.media-del), .ex-navy-modal .ant-btn-dangerous:not(.media-del):hover, .ex-navy-modal .ant-btn-dangerous:not(.media-del):focus {
          border-color:transparent !important; background:transparent !important; box-shadow:none !important;
        }
        /* ...except the media remove pills, which keep an orange-bordered white circle. */
        .ex-navy-modal .media-del, .ex-navy-modal .media-del:hover, .ex-navy-modal .media-del:focus {
          border:1px solid #f5881f !important; background:#fff !important; box-shadow:0 1px 3px rgba(0,0,0,0.15) !important;
          border-radius:50% !important;
        }
      `}</style>
      <Row justify='space-between' align='middle' style={{ marginBottom: 16 }}>
        <Col>
          <h2 style={{ margin: 0, color: '#001429', fontWeight: 700 }}>Exercise Library</h2>
          <span style={{ color: subHeadingColor }}>Define exercises, media, and discipline associations.</span>
        </Col>
        <Col>
          <Button type='primary' icon={<PlusOutlined />} onClick={openCreate}>
            Add exercise
          </Button>
        </Col>
      </Row>

      <Input
        allowClear
        prefix={<SearchOutlined style={{ color: '#bbb' }} />}
        placeholder='Search by name, type, or discipline'
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ maxWidth: 360, marginBottom: 16 }}
      />

      <Table
        rowKey={(r) => r._id}
        loading={loading}
        columns={columns}
        dataSource={filteredExercises}
        pagination={{ pageSize: 20 }}
        onRow={(record) => ({
          onClick: () => setViewExercise(record),
          style: { cursor: 'pointer' },
        })}
      />

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                background: '#e6f4ff',
                color: '#001429',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 15,
              }}
            >
              {form._id ? <EditOutlined /> : <PlusOutlined />}
            </span>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.2 }}>
                {form._id ? 'Edit exercise' : 'New exercise'}
              </div>
              <div style={{ fontSize: 12, color: subHeadingColor, fontWeight: 400 }}>
                {form._id ? `ID ${form.exerciseId || ''} · ${form.type}` : 'Define a new exercise'}
              </div>
            </div>
          </div>
        }
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        okText={form._id ? 'Save changes' : 'Create exercise'}
        okButtonProps={{ size: 'large', style: { borderRadius: 8, fontWeight: 600 } }}
        cancelButtonProps={{ size: 'large', style: { borderRadius: 8 } }}
        width={800}
        wrapClassName='ex-navy-modal'
        destroyOnClose
        bodyStyle={{
          background: '#f6f8fb',
          padding: 20,
          maxHeight: '72vh',
          overflowY: 'auto',
        }}
      >
        {/* BASIC DETAILS */}
        <div style={cardStyle}>
          {cardTitle(<ProfileOutlined />, 'BASIC DETAILS')}
          <div style={{ marginBottom: 16 }}>
            <div style={fieldLabelStyle}>Name</div>
            <Input
              size='large'
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder='e.g. Seated Hamstring Stretch'
            />
          </div>

          <div>
            <div style={fieldLabelStyle}>Description</div>
            <Input.TextArea
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              rows={3}
              placeholder='Short instruction shown to the patient'
            />
          </div>
        </div>

        {/* DISCIPLINES & TYPE */}
        <div style={cardStyle}>
          {cardTitle(<ApartmentOutlined />, 'DISCIPLINES')}
          <Row gutter={16}>
            <Col span={14}>
              <div style={fieldLabelStyle}>Disciplines (surgery types)</div>
              <Select
                mode='multiple'
                size='large'
                style={{ width: '100%' }}
                value={form.surgeryTypeIds}
                onChange={(v) => setField('surgeryTypeIds', v)}
                placeholder='Associate with one or more surgery types'
                optionFilterProp='children'
              >
                {surgeryTypes.map((s) => (
                  <Select.Option key={s._id} value={s._id}>
                    {s.name}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col span={10}>
              <div style={fieldLabelStyle}>Type</div>
              <Radio.Group
                value={form.type}
                onChange={(e) => setField('type', e.target.value)}
                size='large'
                buttonStyle='solid'
              >
                <Radio.Button value='standard'>Standard</Radio.Button>
                <Radio.Button value='TracPatch'>TracPatch</Radio.Button>
              </Radio.Group>
            </Col>
          </Row>
        </div>

        {/* MEDIA */}
        <div style={cardStyle}>
          {cardTitle(<PictureOutlined />, 'MEDIA')}

          {/* Main image */}
          <div style={{ marginBottom: 18 }}>
            <div style={fieldLabelStyle}>Main image</div>
            <span style={mediaHint}>Up to 510×510 px, PNG or JPEG, ≤ 2 MB.</span>
            {form.image ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img src={toMediaUrl(form.image)} alt='' style={mediaThumb} />
                <Button
                  size='small'
                  danger
                  shape='circle' className='media-del'
                  icon={<DeleteFilled />}
                  onClick={() => setField('image', '')}
                  style={{ position: 'absolute', top: -8, right: -8, background: '#fff' }}
                />
              </div>
            ) : (
              <Upload beforeUpload={beforeMainImage} showUploadList={false} accept='image/png,image/jpeg'>
                <Button icon={<UploadOutlined />} type='dashed' style={{ height: 76, borderRadius: 10 }}>
                  Upload main image
                </Button>
              </Upload>
            )}
          </div>

          {/* Image sequence */}
          <div style={{ marginBottom: 18 }}>
            <div style={fieldLabelStyle}>Image sequence (steps)</div>
            <span style={mediaHint}>
              Ordered steps. Each up to 510×510 px, PNG or JPEG, ≤ 2 MB. Up to {MAX_SEQUENCE}.
            </span>
            <Space wrap size={12}>
              {form.imagesequence.map((p, idx) => (
                <div key={`${p}_${idx}`} style={{ textAlign: 'center' }}>
                  <div style={{ position: 'relative' }}>
                    <img src={toMediaUrl(p)} alt='' style={mediaThumb} />
                    <span
                      style={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        background: 'rgba(15,23,42,0.75)',
                        color: '#fff',
                        fontSize: 11,
                        borderRadius: 6,
                        padding: '0 6px',
                      }}
                    >
                      {idx + 1}
                    </span>
                    <Button
                      size='small'
                      danger
                      shape='circle' className='media-del'
                      icon={<DeleteFilled />}
                      onClick={() => removeSequenceAt(idx)}
                      style={{ position: 'absolute', top: -8, right: -8, background: '#fff' }}
                    />
                  </div>
                  <Space size={2} style={{ marginTop: 4 }}>
                    <Button size='small' disabled={idx === 0} onClick={() => moveSequence(idx, -1)}>
                      ←
                    </Button>
                    <Button
                      size='small'
                      disabled={idx === form.imagesequence.length - 1}
                      onClick={() => moveSequence(idx, 1)}
                    >
                      →
                    </Button>
                  </Space>
                </div>
              ))}
              {form.imagesequence.length < MAX_SEQUENCE && (
                <Upload beforeUpload={beforeSequenceImage} showUploadList={false} accept='image/png,image/jpeg'>
                  <Button type='dashed' style={uploadTile} icon={<PlusOutlined />} />
                </Upload>
              )}
            </Space>
          </div>

          {/* Video */}
          <div>
            <div style={fieldLabelStyle}>Video</div>
            <span style={mediaHint}>MP4 (H.264), 1280×720 (720p) recommended, ≤ 50 MB.</span>
            {form.video ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <video
                  src={toMediaUrl(form.video)}
                  controls
                  style={{ width: 240, borderRadius: 10, border: '1px solid #e5e7eb', display: 'block' }}
                />
                <Button
                  size='small'
                  danger
                  shape='circle' className='media-del'
                  icon={<DeleteFilled />}
                  onClick={() => setField('video', '')}
                  style={{ position: 'absolute', top: -8, right: -8, background: '#fff' }}
                />
              </div>
            ) : (
              <Upload beforeUpload={beforeVideo} showUploadList={false} accept='video/mp4'>
                <Button icon={<UploadOutlined />} type='dashed' style={{ height: 76, borderRadius: 10 }}>
                  Upload video
                </Button>
              </Upload>
            )}
          </div>
        </div>

        {form.type === 'TracPatch' && (
          <>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 1.2,
                color: '#001429',
                margin: '4px 2px 12px',
              }}
            >
              TRACPATCH CONFIGURATION
            </div>

            {tpLiveError && (
              <div
                style={{
                  color: '#cf1322',
                  fontSize: 12,
                  marginBottom: 12,
                  padding: 10,
                  background: '#fff1f0',
                  border: '1px solid #ffa39e',
                  borderRadius: 8,
                }}
              >
                ⚠ {tpLiveError}. The save will be rejected by the server.
              </div>
            )}

            {/* POSTURE / MODE / BANDS */}
            <div style={cardStyle}>
              {cardTitle(<SettingOutlined />, 'SETUP')}
              <Row gutter={16}>
                <Col span={8}>
                  <div style={fieldLabelStyle}>Posture</div>
                  <Select style={{ width: '100%' }} value={form.tracpatchConfig.posture} onChange={(v) => setTp('posture', v)}>
                    {POSTURES.map((p) => (
                      <Select.Option key={p} value={p}>
                        {p}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
                <Col span={8}>
                  <div style={fieldLabelStyle}>Mode</div>
                  <Select style={{ width: '100%' }} value={form.tracpatchConfig.mode} onChange={(v) => setTp('mode', v)}>
                    {MODES.map((m) => (
                      <Select.Option key={m} value={m}>
                        {m}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
                <Col span={8}>
                  <div style={fieldLabelStyle}>Bands required</div>
                  <Select
                    mode='multiple'
                    style={{ width: '100%' }}
                    value={form.tracpatchConfig.bandsRequired}
                    onChange={(v) => setTp('bandsRequired', v)}
                  >
                    <Select.Option value='ext'>ext</Select.Option>
                    <Select.Option value='flex'>flex</Select.Option>
                  </Select>
                </Col>
              </Row>
            </div>

            {/* ANGLES — tabular: header row + one input row */}
            <div style={cardStyle}>
              {cardTitle(<DashboardOutlined />, 'ANGLES (°)')}
              <div style={tableBox}>
                <Row gutter={8} style={tableHeadRow}>
                  {ANGLE_CHAIN.map(([key, label]) => (
                    <Col span={4} key={key}>
                      <div style={tableHeaderStyle}>{label}</div>
                    </Col>
                  ))}
                </Row>
                <Row gutter={8} style={tableBodyRow}>
                  {ANGLE_CHAIN.map(([key]) => (
                    <Col span={4} key={key}>
                      <InputNumber
                        style={{ width: '100%' }}
                        value={form.tracpatchConfig[key]}
                        onChange={(v) => setTp(key, v)}
                      />
                    </Col>
                  ))}
                </Row>
              </div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
                Must stay non-decreasing left→right (Ext limit ≤ Ext high ≤ Ext low ≤ Flex low ≤ Flex high ≤ Flex limit).
              </div>
            </div>

            {/* TARGETS — tabular */}
            <div style={cardStyle}>
              {cardTitle(<AimOutlined />, 'TARGETS')}
              <div style={tableBox}>
                <Row gutter={8} style={tableHeadRow}>
                  <Col span={6}><div style={tableHeaderStyle}>Rest angle</div></Col>
                  <Col span={6}><div style={tableHeaderStyle}>Required reps</div></Col>
                  <Col span={6}><div style={tableHeaderStyle}>Hold seconds</div></Col>
                  <Col span={6}><div style={tableHeaderStyle}>Sets</div></Col>
                </Row>
                <Row gutter={8} style={tableBodyRow}>
                  <Col span={6}>
                    <InputNumber style={{ width: '100%' }} value={form.tracpatchConfig.restAngle} onChange={(v) => setTp('restAngle', v)} />
                  </Col>
                  <Col span={6}>
                    <InputNumber style={{ width: '100%' }} min={0} value={form.tracpatchConfig.requiredReps} onChange={(v) => setTp('requiredReps', v)} />
                  </Col>
                  <Col span={6}>
                    <InputNumber style={{ width: '100%' }} min={0} value={form.tracpatchConfig.requiredHoldSeconds} onChange={(v) => setTp('requiredHoldSeconds', v)} />
                  </Col>
                  <Col span={6}>
                    <InputNumber style={{ width: '100%' }} min={0} value={form.tracpatchConfig.requiredSets} onChange={(v) => setTp('requiredSets', v)} />
                  </Col>
                </Row>
              </div>
            </div>

            {/* TIER DEFAULTS — bordered table, one row per tier */}
            <div style={cardStyle}>
              {cardTitle(<TableOutlined />, 'TIER DEFAULTS (PER-WEEK TABLE)')}
              <div style={tableBox}>
                <Row gutter={6} style={tableHeadRow}>
                  <Col span={2}><div style={tableHeaderStyle}>Tier</div></Col>
                  <Col span={2}><div style={{ ...tableHeaderStyle, whiteSpace: 'normal' }}>Week from</div></Col>
                  <Col span={2}><div style={{ ...tableHeaderStyle, whiteSpace: 'normal' }}>Week to</div></Col>
                  <Col span={3}><div style={tableHeaderStyle}>FlexH</div></Col>
                  <Col span={3}><div style={tableHeaderStyle}>FlexL</div></Col>
                  <Col span={3}><div style={tableHeaderStyle}>ExtL</div></Col>
                  <Col span={3}><div style={tableHeaderStyle}>ExtH</div></Col>
                  <Col span={2}><div style={tableHeaderStyle}>Reps</div></Col>
                  <Col span={3}><div style={tableHeaderStyle}>Milestone</div></Col>
                  <Col span={1} />
                </Row>
                {(form.tracpatchConfig.tierDefaults || []).length === 0 && (
                  <div style={{ padding: '12px', color: '#94a3b8', fontSize: 12 }}>
                    No tier rows — the master angles above apply for every week.
                  </div>
                )}
                {(form.tracpatchConfig.tierDefaults || []).map((row, idx) => (
                  <Row gutter={6} key={idx} style={tableBodyRow} align='middle'>
                    <Col span={2}>
                      <InputNumber style={{ width: '100%' }} value={row.tier} onChange={(v) => setTierField(idx, 'tier', v)} />
                    </Col>
                    <Col span={2}>
                      <InputNumber style={{ width: '100%' }} value={row.weekFrom} onChange={(v) => setTierField(idx, 'weekFrom', v)} />
                    </Col>
                    <Col span={2}>
                      <InputNumber style={{ width: '100%' }} value={row.weekTo} onChange={(v) => setTierField(idx, 'weekTo', v)} />
                    </Col>
                    <Col span={3}>
                      <InputNumber style={{ width: '100%' }} value={row.flexion_high} onChange={(v) => setTierField(idx, 'flexion_high', v)} />
                    </Col>
                    <Col span={3}>
                      <InputNumber style={{ width: '100%' }} value={row.flexion_low} onChange={(v) => setTierField(idx, 'flexion_low', v)} />
                    </Col>
                    <Col span={3}>
                      <InputNumber style={{ width: '100%' }} value={row.extension_low} onChange={(v) => setTierField(idx, 'extension_low', v)} />
                    </Col>
                    <Col span={3}>
                      <InputNumber style={{ width: '100%' }} value={row.extension_high} onChange={(v) => setTierField(idx, 'extension_high', v)} />
                    </Col>
                    <Col span={2}>
                      <InputNumber style={{ width: '100%' }} min={0} value={row.reps} onChange={(v) => setTierField(idx, 'reps', v)} />
                    </Col>
                    <Col span={3}>
                      <Select
                        style={{ width: '100%' }}
                        value={row.rep_milestone}
                        onChange={(v) => setTierField(idx, 'rep_milestone', v)}
                      >
                        {MILESTONES.map((m) => (
                          <Select.Option key={m} value={m}>
                            {m}
                          </Select.Option>
                        ))}
                      </Select>
                    </Col>
                    <Col span={1}>
                      <Button danger size='small' icon={<DeleteFilled />} onClick={() => removeTier(idx)} />
                    </Col>
                  </Row>
                ))}
              </div>
              <Button size='small' icon={<PlusOutlined />} onClick={addTier} style={{ marginTop: 10 }}>
                Add tier row
              </Button>
            </div>
          </>
        )}
      </Modal>

      <ExerciseInstructionsModal
        visible={!!viewExercise}
        exercise={viewExercise}
        onClose={() => setViewExercise(null)}
      />
    </div>
  )
}
